import React, { useState } from "react"
import { saveCodeToInbox, resendCodeToInbox, ApiError } from "../lib/api"
import { HONEYPOT_FIELD } from "../lib/botProtection"
import useBotProtection from "../hooks/useBotProtection"
import { validateEmail } from "../lib/email"
import Turnstile from "./Turnstile"

// Reassurance footnote shown beneath the capture: the bot-protection note plus
// Privacy / Help links. The real <Turnstile> widget (above) does the work; this
// is the always-visible context. Lucide shield, per the brand icon set.
function ProtectionNote() {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[color:var(--color-muted)] dark:text-muted-light">
      <span className="inline-flex items-center gap-1.5">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0 text-voltage-deep dark:text-voltage-light">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" />
        </svg>
        Protected by Turnstile
      </span>
      <a href="/privacy" className="underline underline-offset-2 hover:text-coal dark:hover:text-cloud focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 rounded">Privacy</a>
      <a href="/contact" className="underline underline-offset-2 hover:text-coal dark:hover:text-cloud focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 rounded">Help</a>
    </div>
  )
}

// The panel's value proposition: why an anonymous visitor should hand over an
// email. Lucide glyphs (pencil, shield-check, bar-chart-3, check).
const BENEFITS = [
  {
    title: "Repoint it anytime",
    body: "Change where it goes without reprinting.",
    icon: <><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" /></>
  },
  {
    title: "It's tied to you",
    body: "Sign in later with this email to manage it.",
    icon: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" /></>
  },
  {
    title: "See your scans",
    body: "Who's scanning and where, sent to your inbox.",
    icon: <><path d="M3 3v16a2 2 0 0 0 2 2h16" /><path d="M18 17V9" /><path d="M13 17V5" /><path d="M8 17v-3" /></>
  },
  {
    title: "Free, no account",
    body: "One field. We'll email it right over.",
    icon: <path d="M20 6 9 17l-5-5" />
  }
]

function BenefitRow({ title, body, icon }) {
  return (
    <div className="flex items-start gap-[11px]">
      <span className="flex-shrink-0 w-[30px] h-[30px] rounded-[9px] border border-voltage bg-white dark:bg-midnight inline-flex items-center justify-center text-voltage-dark dark:text-voltage-light">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{icon}</svg>
      </span>
      <div className="min-w-0">
        <p className="text-[13.5px] font-semibold text-coal dark:text-cloud">{title}</p>
        <p className="text-sm leading-[1.4] text-[color:var(--color-muted)] dark:text-muted-light">{body}</p>
      </div>
    </div>
  )
}

// Anonymous "save this code to my inbox" capture. `layout="stacked"` is the
// historic tall, centered block; `layout="modal"` is the hero's capture as the
// body of a dismissable dialog (HeroResult owns the <dialog> chrome) — pitch +
// benefits, then a single "Save to my inbox" CTA. Behaviour — validation,
// saveCodeToInbox, honeypot/timing/Turnstile, success state — is identical
// across both; only the wrapper markup and copy differ. `onClose` (modal only)
// dismisses the dialog, both from "Maybe later" and from the success state.
export default function EmailCapture({ token, layout = "stacked", onClose }) {
  const [email, setEmail] = useState("")
  const [status, setStatus] = useState("idle")
  const [error, setError] = useState(null)
  // A bot-layer refusal is not the email's fault, so it renders as its own
  // form-level notice with an escape hatch instead of reddening the address
  // field. Same treatment as the builder's hero form. Issue #166.
  const [verificationError, setVerificationError] = useState(null)
  const [savedAs, setSavedAs] = useState(null)
  const [touched, setTouched] = useState(false)
  // Honeypot + timing stamp + Turnstile token, and the recovery all three need
  // after a refusal — shared with QrBuilder (same server-side check).
  const bot = useBotProtection()
  // Resend ("didn't get it?") lives only in the done state; its own status so a
  // resend attempt never disturbs the primary capture flow above.
  const [resendStatus, setResendStatus] = useState("idle")
  const [resendError, setResendError] = useState(null)

  const modal = layout === "modal"

  const handleSubmit = (e) => {
    e.preventDefault()
    const validation = validateEmail(email)
    setTouched(true)
    if (validation) {
      setError(validation)
      setStatus("error")
      return
    }
    // Before the browser check has a token, the press waits for it (#339).
    bot.submitWhenReady(send)
  }

  const send = async () => {
    setStatus("submitting")
    setError(null)
    setVerificationError(null)
    try {
      const res = await saveCodeToInbox(token, email.trim(), bot.fields())
      setSavedAs(res.email)
      setStatus("done")
    } catch (e) {
      const apiErr = e instanceof ApiError ? e : new ApiError("network", e.message)
      if (apiErr.kind === "verification_failed") {
        // Clear every signal the visitor could be stuck behind (spent token,
        // autofilled honeypot, stale timing stamp) so the retry can pass.
        bot.recover()
        setVerificationError(apiErr.message)
      } else {
        setError(apiErr.message)
      }
      setStatus("error")
    }
  }

  const handleBlur = () => {
    setTouched(true)
    if (status !== "done") setError(validateEmail(email))
  }

  const handleResend = async () => {
    setResendStatus("sending")
    setResendError(null)
    try {
      await resendCodeToInbox(token)
      setResendStatus("sent")
    } catch (e) {
      const apiErr = e instanceof ApiError ? e : new ApiError("network", e.message)
      setResendError(apiErr.message)
      setResendStatus("error")
    }
  }

  // "Didn't get it? Resend" — shown under both done states. Once re-sent it
  // confirms inline; a throttle/network failure surfaces the server message.
  const resendPrompt = (
    <p className="mt-3 text-sm text-[color:var(--color-muted)] dark:text-muted-light" aria-live="polite">
      {resendStatus === "sent" ? (
        "Sent again — check your inbox."
      ) : resendStatus === "error" ? (
        <span className="text-[color:var(--color-fail)]">{resendError}</span>
      ) : (
        <>
          Didn't get it?{" "}
          <button
            type="button"
            onClick={handleResend}
            disabled={resendStatus === "sending"}
            className="font-semibold text-voltage-dark dark:text-voltage-light underline underline-offset-2 hover:text-coal dark:hover:text-cloud disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 rounded"
          >
            {resendStatus === "sending" ? "Resending…" : "Resend it"}
          </button>
        </>
      )}
    </p>
  )

  const submitting = status === "submitting"
  const done = status === "done"
  const showError = (touched && error) || (status === "error" && error)

  // ── Done (stacked only) ─────────────────────────────────────────────────
  // The modal keeps its own done state (with a "View my code" dismiss), so it
  // is rendered inline below rather than short-circuiting here.
  if (done && !modal) {
    return (
      <div className="text-center w-full" aria-live="polite">
        <p className="font-display text-lg text-ink dark:text-cloud">✓ Sent. Check your inbox.</p>
        <p className="mt-1 text-sm text-[color:var(--color-muted)] dark:text-muted-light">
          Sign in later with <strong className="text-ink dark:text-cloud">{savedAs}</strong> to edit it.
        </p>
        {resendPrompt}
      </div>
    )
  }

  // ── Shared field elements ────────────────────────────────────────────────
  // Padding and width are passed in per layout: Tailwind resolves conflicting
  // utilities by CSS order, not class-list order, so the two layouts must not
  // both emit a padding class.
  const renderInput = (extra) => (
    <input
      id="capture-email"
      name="email"
      type="email"
      inputMode="email"
      autoComplete="email"
      placeholder="you@example.com"
      value={email}
      onChange={(e) => {
        setEmail(e.target.value)
        if (touched) setError(null)
      }}
      onBlur={handleBlur}
      disabled={submitting || bot.held}
      aria-invalid={Boolean(showError)}
      aria-describedby={showError ? "capture-email-error" : undefined}
      className={
        "min-w-0 rounded border bg-white dark:bg-midnight text-ink dark:text-cloud text-base " +
        "placeholder:text-[color:var(--color-muted)] dark:placeholder:text-muted-light " +
        "focus:outline-none focus:ring-2 focus:ring-voltage focus:border-voltage " +
        "disabled:opacity-60 disabled:cursor-not-allowed " +
        (showError
          ? "border-[color:var(--color-fail)] "
          : "border-[color:var(--color-border)] dark:border-storm ") +
        extra
      }
    />
  )

  const renderSubmit = (extra, label) => (
    <button
      type="submit"
      // While the browser check has no token (first load, or a reset after a
      // refusal), a press is held and sent when it lands, never sent empty.
      // Locked once one is held, so it goes once.
      disabled={submitting || bot.held}
      className={
        "rounded-lg bg-voltage text-coal font-semibold shadow-voltage " +
        "transition hover:bg-voltage-dark disabled:opacity-60 disabled:cursor-not-allowed " +
        extra
      }
    >
      {submitting ? "Saving…" : bot.turnstilePending || bot.held ? "Checking your browser…" : label}
    </button>
  )

  const errorEl = showError && (
    <p id="capture-email-error" className="mt-2 text-sm text-[color:var(--color-fail)]">
      {error}
    </p>
  )

  // Form-level, because the address is fine — the bot layer is what refused.
  // Turnstile has a real false-positive rate on humans (VPNs, privacy
  // browsers, corporate proxies), so this carries the same /contact escape
  // hatch the builder's form error does. role="alert" announces it without
  // moving focus. Issue #166.
  const verificationErrorEl = verificationError && (
    <div
      role="alert"
      className="mt-3 rounded border border-[color:var(--color-fail)] px-3.5 py-3 text-sm text-[color:var(--color-fail)]"
    >
      {verificationError}{" "}
      <a href="/contact" className="underline hover:no-underline">
        Still stuck? Get in touch
      </a>{" "}
      and we'll send your code.
    </div>
  )

  // Honeypot — visually hidden, off the tab order; bots fill it, people don't.
  const honeypotEl = (
    <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
      <label htmlFor="email-capture-company">Company (leave this blank)</label>
      <input
        id="email-capture-company"
        name={HONEYPOT_FIELD}
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={bot.honeypot}
        onChange={(e) => bot.setHoneypot(e.target.value)}
      />
    </div>
  )

  // ── Modal (hero) ──────────────────────────────────────────────────────────
  // The body of HeroResult's dismissable dialog: the value proposition lands as
  // a deliberate step before the user reaches the code, with a single primary
  // CTA. On success, or via "Maybe later", `onClose` drops the user onto the
  // live code (QR + downloads) behind it.
  if (modal) {
    if (done) {
      return (
        <div className="text-center px-1 py-2" aria-live="polite">
          <span className="mx-auto flex-shrink-0 w-12 h-12 rounded-full bg-voltage text-coal inline-flex items-center justify-center">
            <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
          </span>
          <h3 className="mt-4 font-display font-bold text-[21px] tracking-[-0.01em] text-coal dark:text-cloud">
            Saved to your inbox.
          </h3>
          <p className="mt-2 text-[14.5px] leading-[1.5] text-storm dark:text-muted-light">
            Check your email. Sign in later with{" "}
            <strong className="font-semibold text-coal dark:text-cloud">{savedAs}</strong> to edit this code.
          </p>
          {resendPrompt}
          <button
            type="button"
            onClick={onClose}
            className="mt-5 w-full rounded-lg bg-voltage text-coal font-semibold shadow-voltage transition hover:bg-voltage-dark px-5 py-[13px] text-base min-h-[48px] focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2"
          >
            View my code
          </button>
        </div>
      )
    }
    return (
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-plasma dark:text-plasma-light">
          Save to your inbox
        </p>
        <h3 className="mt-1.5 font-display font-bold text-[23px] tracking-[-0.01em] text-coal dark:text-cloud">
          Keep this code yours.
        </h3>
        <p className="mt-2 text-[14.5px] leading-[1.5] text-storm dark:text-muted-light">
          Add your email and this exact code lands in your inbox. Free, no account.
        </p>

        <div className="mt-[18px] grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3">
          {BENEFITS.map((b) => <BenefitRow key={b.title} {...b} />)}
        </div>

        <form onSubmit={handleSubmit} noValidate className="mt-5">
          <label htmlFor="capture-email" className="block mb-[9px] text-sm font-semibold text-coal dark:text-cloud">
            Email me this code
          </label>
          {renderInput("w-full px-3.5 py-3")}
          {errorEl}
          {renderSubmit("w-full mt-[11px] px-5 py-[13px] text-base min-h-[48px]", "Save to my inbox")}
          {verificationErrorEl}
          {honeypotEl}
        </form>

        <button
          type="button"
          onClick={onClose}
          className="mt-3 w-full text-center text-sm font-semibold text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud transition focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 rounded"
        >
          Maybe later
        </button>

        <Turnstile {...bot.turnstileProps} />
        <ProtectionNote />
      </div>
    )
  }

  // ── Stacked (default) ─────────────────────────────────────────────────────
  return (
    <form onSubmit={handleSubmit} noValidate className="w-full">
      <p className="font-display text-base text-ink dark:text-cloud text-center">
        Want to come back to this later?
      </p>
      <p className="mt-1 text-sm text-[color:var(--color-muted)] dark:text-muted-light text-center">
        We'll send the code to your inbox.
      </p>

      <label htmlFor="capture-email" className="sr-only">Email address</label>
      <div className="mt-3 flex flex-col sm:flex-row gap-2">
        {renderInput("flex-1 px-4 py-3")}
        {renderSubmit("px-5 py-3 min-h-[44px] whitespace-nowrap", "Save to inbox")}
      </div>

      {errorEl}
      {verificationErrorEl}
      {honeypotEl}

      <Turnstile {...bot.turnstileProps} />
    </form>
  )
}
